/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Card, Container, Button } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import ItemCardList from "../../components/Modules/ItemCardList";
import TenantAPI from "../../api/api/TenantAPI";
import EditModuleModal from "../../components/Modals/EditModuleModal";
import { modulesAtom } from "../../atoms/GlobalAtoms";
import useToggle from "../../hooks/useToggle";
import AppPageHeader from "../../components/Headers/AppPageHeader";
import { setSchemaAtom, tenantIDAtom } from "../../atoms/GlobalAtoms";
import { BasicSearch } from "../cloud/CloudSettingsHome";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import { Module } from "../../types/TenantInterfaces";
import AppNavbar from "../../components/AppNavbar/AppNavbar";
import DataTransformsView from "./DataTransforms";
import DataTransformsDrawer from "../../components/Drawers/DataTransformsDrawer";
import QueryBuilderDrawer from "../../components/QueryBuilder/QueryBuilderDrawer";

const tabs = [
  { title: "Data Sources", activeValue: "sources" },
  { title: "Data Transforms", activeValue: "transforms" },
];

const CRMModuleFieldSettingsView = () => {
  const tenantID = useAtomValue(tenantIDAtom);
  const navigate = useNavigate();
  const [addModuleLoader, setAddModuleLoader] = useState(false);
  const [createModal, toggleCreateModal, setCreateModal] = useToggle();
  const [dataTransformsDrawer, toggleDataTransformsDrawer] = useToggle();
  const [createError, setCreateError] = useState<string | null>(null);
  const setSchema = useSetAtom(setSchemaAtom);
  const modules = useAtomValue(modulesAtom);
  const [params] = useSearchParams();
  const [searchValue, setSearchValue] = useState(params.get("search") || "");
  const [activeTab, setActiveTab] = useState<"sources" | "transforms">(
    "sources"
  );
  const [openSQLBuilder, setOpenSQLBuilder] = useState(false);

  const filteredModules = Array.isArray(modules)
    ? modules.filter(searchFilter)
    : [];

  filteredModules.sort((a, b) => a?.Name?.localeCompare?.(b?.Name));

  const customModules: Module[] = [];
  const defaultModules: Module[] = [];
  const dynamicModules: Module[] = [];

  filteredModules.forEach((mod) => {
    if (mod?.Default) {
      defaultModules.push(mod);
    }
    if (mod?.Dynamic) {
      dynamicModules.push(mod);
    } else {
      customModules.push(mod);
    }
  });

  return (
    <>
      <AppHelmet
        title={`Settings - ${
          activeTab === "sources" ? "Data Sources" : "Data Transforms"
        }`}
      />
      <div className="header">
        <Container fluid>
          <div className="header-body">
            <AppPageHeader
              title={
                activeTab === "sources" ? "Data Sources" : "Data Transforms"
              }
              description={
                activeTab === "sources"
                  ? "Create and manage data sources available in your Cloud"
                  : "Simplify data conversions form improved record management"
              }
              customContent={
                <div
                  className="d-flex justify-content-between align-self-end"
                  style={{ width: 450 }}
                >
                  <BasicSearch
                    useQueryParams
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    placeholder={`Search data ${activeTab}`}
                  />
                  <Button
                    className="ml-2"
                    color="primary"
                    onClick={
                      activeTab === "sources"
                        ? toggleCreateModal
                        : toggleDataTransformsDrawer
                    }
                  >
                    Add
                  </Button>
                </div>
              }
            />
            <EditModuleModal
              open={createModal}
              toggle={toggleCreateModal}
              onUpdate={handleAddModule}
              submitting={addModuleLoader}
              error={createError}
              onClose={() => {
                setCreateModal(false);
              }}
              mode="create"
              setQueryBuilder={setOpenSQLBuilder}
            />
          </div>
        </Container>
      </div>

      <div className="ml-3 mb-3">
        {/* @ts-ignore */}
        <AppNavbar
          tabs={tabs}
          activeTab={activeTab}
          onSelect={setActiveTab}
        ></AppNavbar>
      </div>

      {activeTab === "sources" ? (
        <Container fluid>
          <Card>
            <h3 className="mb-1 pl-2 text-uppercase text-light">
              Custom Data Sources
            </h3>
            {customModules?.length > 0 ? (
              <>
                {customModules.map((module, i) => {
                  if (module?.Default) return null;

                  return (
                    <div key={module.APIName}>
                      <ItemCardList
                        item={{ Name: module.Name }}
                        icon="mdi-database-outline"
                        link={`/clouds/${tenantID}/settings/modules/manage/${module.APIName}/fields`}
                        selectActionType="redirectable"
                        rightIcon="mdi mdi-chevron-right"
                      />
                      {modules && modules.length - 1 !== i && (
                        <>
                          <hr className="my-0"></hr>
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="m-2 bg-ultralight border rounded p-3 font-weight-500 text-dark mt-3">
                No Data Sources Found.
              </div>
            )}
            <h3 className="mt-3 mb-1 pl-2 text-uppercase text-light">
              Dynamic Data Sources
            </h3>
            {!!dynamicModules?.length ? (
              <>
                {dynamicModules?.map((module, i) => {
                  return (
                    <div key={`${module.APIName}-data-source`}>
                      <ItemCardList
                        item={{ Name: module.Name }}
                        icon="mdi-database-outline"
                        link={`/clouds/${tenantID}/settings/modules/manage/${module.APIName}/query`}
                        selectActionType="redirectable"
                        rightIcon="mdi mdi-chevron-right"
                      />
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                <div className="m-2 bg-ultralight border rounded p-3 font-weight-500 text-dark mt-3">
                  No Dynamic Data Sources Found.
                </div>
              </>
            )}
            <h3 className="mt-3 mb-1 pl-2 text-uppercase text-light">
              System Defaults
            </h3>
            {defaultModules?.length > 0 ? (
              <>
                {defaultModules.map((module, i) => {
                  if (!module?.Default) return null;

                  return (
                    <div key={`${module.APIName}-data-source`}>
                      <ItemCardList
                        item={{ Name: module.Name }}
                        icon="mdi-database-outline"
                        link={`/clouds/${tenantID}/settings/modules/manage/${module.APIName}/fields`}
                        selectActionType="redirectable"
                        rightIcon="mdi mdi-chevron-right"
                      />
                      {modules && modules.length - 1 !== i && (
                        <>
                          <hr className="my-0"></hr>
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="m-2 bg-ultralight border rounded p-3 font-weight-500 text-dark mt-3">
                No Data Sources Found.
              </div>
            )}
          </Card>
        </Container>
      ) : (
        <>
          <Container fluid>
            <DataTransformsView searchQuery={searchValue} />
            <DataTransformsDrawer
              mode="create"
              open={dataTransformsDrawer}
              closeDrawer={toggleDataTransformsDrawer}
            />
          </Container>
        </>
      )}
      <QueryBuilderDrawer
        open={openSQLBuilder}
        close={() => setOpenSQLBuilder(false)}
      />
    </>
  );

  function searchFilter(module: Module) {
    if (!searchValue) return true;
    if (!module || !module?.APIName) return false;

    const lowerCaseSearch = searchValue.toLowerCase();
    const lowerCaseName = module.Name.toLowerCase();
    const lowerCaseAPI = module.APIName.toLowerCase();

    return (
      lowerCaseName.startsWith(lowerCaseSearch) ||
      lowerCaseAPI.startsWith(lowerCaseSearch)
    );
  }

  function handleAddModule(
    values: any,
    setValues: (data: any) => void,
    initialValues: any
  ) {
    //Exception handling on createName and createAPIName ?
    setAddModuleLoader(true);
    setCreateError(null);

    TenantAPI.addCRMModule(values.Name, values.APIName)
      .then(
        (data) => {
          const newSchema = data.data.data.schema;
          setSchema(newSchema);

          navigate(
            `/clouds/${tenantID}/settings/modules/manage/${values.APIName}/fields`
          );
          setValues(initialValues);
          toggleCreateModal();
        },
        (err) => {
          if (err.response.data && err.response.data.message) {
            setCreateError(err.response.data.message);
            console.log(err.response.data.message);
          } else {
            setCreateError("An unknown error occurred. Please try again.");
          }
        }
      )
      .finally(() => {
        setAddModuleLoader(false);
      });
  }
};

export default CRMModuleFieldSettingsView;
